import React, { useEffect, useState, useRef } from 'react';
import { getSites, getPlugins, getThemes, updatePlugin, updateTheme, deployPrepare, deployToSite } from '../api';
import { Upload as UploadIcon, FileArchive, Globe, RefreshCw, Zap, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/Button';
import Flag from '../components/Flag';


export default function Upload() {
  const [sites, setSites]           = useState([]);
  const [allPlugins, setAllPlugins] = useState([]); // {name, file, site_id, site_name, country, version, new_version, update_available}
  const [allThemes, setAllThemes]   = useState([]);
  const [type, setType]             = useState('plugin');
  const [file, setFile]             = useState(null);
  const [selected, setSelected]     = useState([]);
  const [deploying, setDeploying]   = useState(false);
  const [updating, setUpdating]     = useState(false);
  const [log, setLog]               = useState([]);
  const [progress, setProgress]     = useState(0);
  const [selectedPlugin, setSelectedPlugin] = useState(null); // chosen plugin filter
  const [pluginDropOpen, setPluginDropOpen] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoadingData(true);
    setLog([]);
    try {
      const s = await getSites();
      setSites(s);
      if (!s.length) { toast.error('No sites connected. Add sites first.'); return; }

      addLog(`→ Loading data from ${s.length} sites...`, 'info');

      // Load plugins from all sites in parallel
      const pluginResults = await Promise.allSettled(
        s.map(site =>
          getPlugins(site.id)
            .then(ps => ps.map(p => ({
              ...p,
              site_id: site.id,
              site_name: site.name,
              country: site.country || 'Other'
            })))
            .catch(err => {
              console.warn(`Plugin load failed for ${site.name}:`, err);
              return [];
            })
        )
      );
      const plugins = pluginResults.flatMap(r => r.status==='fulfilled' ? r.value : []);
      setAllPlugins(plugins);

      // Load themes from all sites in parallel
      const themeResults = await Promise.allSettled(
        s.map(site =>
          getThemes(site.id)
            .then(ts => ts.map(t => ({
              ...t,
              site_id: site.id,
              site_name: site.name,
              country: site.country || 'Other'
            })))
            .catch(err => {
              console.warn(`Theme load failed for ${site.name}:`, err);
              return [];
            })
        )
      );
      const themes = themeResults.flatMap(r => r.status==='fulfilled' ? r.value : []);
      setAllThemes(themes);

      const updateCount = plugins.filter(p=>p.update_available).length;
      addLog(`✓ Loaded ${plugins.length} plugins, ${themes.length} themes from ${s.length} sites`, 'ok');
      addLog(`→ ${updateCount} plugin updates available across all sites`, 'info');
    } catch(err) {
      console.error(err);
      addLog(`✗ Error loading data: ${err.message}`, 'err');
    }
    finally { setLoadingData(false); }
  };

  const addLog = (msg, cls='info') => setLog(prev => [...prev, { msg, cls }]);

  // Unique plugin names across all sites
  const uniquePlugins = [...new Map(
    allPlugins.map(p => [p.name, { name: p.name, file: p.file }])
  ).values()].sort((a,b)=>a.name.localeCompare(b.name));

  const uniqueThemes = [...new Map(
    allThemes.map(t => [t.name, { name: t.name, slug: t.slug }])
  ).values()].sort((a,b)=>a.name.localeCompare(b.name));

  // ALL sites that have the selected plugin/theme installed (so a custom .zip
  // can be deployed to any of them — premium plugins never report an update
  // via the WP repo, so we must not hide up-to-date sites here).
  const sitesAll = !selectedPlugin ? [] : (type === 'plugin'
    ? allPlugins.filter(p => p.name === selectedPlugin.name)
    : allThemes.filter(t => t.name === selectedPlugin.name));

  // Subset that WordPress reports an update for (used by bulk repo update)
  const sitesWithUpdate = sitesAll.filter(s => s.update_available);

  const handlePluginSelect = (item) => {
    setSelectedPlugin(item);
    setPluginDropOpen(false);
    setSelected([]);
    setLog([]);
    setProgress(0);
  };

  const toggleSite = (siteId) => {
    setSelected(s => s.includes(siteId) ? s.filter(x=>x!==siteId) : [...s, siteId]);
  };

  const selectAll = () => setSelected(sitesAll.map(p => p.site_id));
  const selectNeedingUpdate = () => setSelected(sitesWithUpdate.map(p => p.site_id));

  // Bulk update via WP repo (no zip needed) — only for sites WordPress
  // reports an update for. Premium plugins have none → guide to zip deploy.
  const handleBulkUpdate = async () => {
    if (!selectedPlugin || !selected.length) return toast.error('Select plugin and sites first');
    const targets = selected.filter(id => {
      const s = sitesAll.find(p => p.site_id === id);
      return s && s.update_available;
    });
    if (!targets.length) {
      toast.error(`No WordPress-repo update for “${selectedPlugin.name}” on the selected sites. For premium plugins, upload the .zip and use “Run deploy” instead.`, { duration: 8000 });
      return;
    }
    setUpdating(true);
    setLog([]);
    setProgress(0);
    addLog(`→ Bulk updating "${selectedPlugin.name}" on ${targets.length} site(s) with a repo update...`);

    let done = 0;
    for (const siteId of targets) {
      const siteInfo = sitesAll.find(p => p.site_id === siteId);
      const siteName = siteInfo?.site_name || siteId;
      try {
        if (!siteInfo) { addLog(`✗ ${siteName} — plugin not found on site`, 'err'); continue; }
        addLog(`→ Updating on ${siteName}...`, 'info');
        if (type === 'plugin') {
          await updatePlugin(siteId, siteInfo.file);
        } else {
          await updateTheme(siteId, siteInfo.slug);
        }
        done++;
        setProgress(Math.round((done/targets.length)*100));
        addLog(`✓ ${siteName} — updated ${siteInfo.version} → ${siteInfo.new_version}`, 'ok');
        // Remove from list
        if (type === 'plugin') {
          setAllPlugins(prev => prev.map(p =>
            p.site_id===siteId && p.name===selectedPlugin.name ? {...p, update_available:false} : p
          ));
        } else {
          setAllThemes(prev => prev.map(t =>
            t.site_id===siteId && t.name===selectedPlugin.name ? {...t, update_available:false} : t
          ));
        }
      } catch(err) {
        addLog(`✗ ${siteName} — ${err?.error || err?.message || 'failed'}`, 'err');
      }
    }
    addLog(`\n✓ Done! ${done}/${targets.length} sites updated.`, 'ok');
    toast.success(`Updated ${done}/${targets.length} sites`);
    setUpdating(false);
  };

  // ZIP deploy — upload the file ONCE to the dashboard, then each site
  // downloads it itself (bypasses host firewalls that 403 big uploads, and
  // avoids proxy timeouts since each per-site request is tiny).
  const handleDeploy = async () => {
    if (!file) return toast.error('Select a .zip file first');
    if (!selected.length) return toast.error('Select at least one site');
    setDeploying(true); setLog([]); setProgress(0);

    // 1) Upload once to the dashboard
    let token;
    addLog(`→ Uploading ${file.name}…`);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('type', type);
      const r = await deployPrepare(form);
      token = r.token;
      addLog(`✓ Uploaded. Each site will download & install it (bypasses upload firewalls). This can take a few minutes…`, 'ok');
    } catch (err) {
      addLog(`✗ Upload to dashboard failed: ${err?.error || err?.message || 'failed'}`, 'err');
      toast.error('Upload failed');
      setDeploying(false);
      return;
    }

    // 2) Tell each selected site to fetch + install it
    let done = 0, ok = 0;
    for (const siteId of selected) {
      const site = sitesAll.find(s => s.site_id === siteId);
      const name = site?.site_name || `Site ${siteId}`;
      try {
        addLog(`→ ${name}…`, 'info');
        const r = await deployToSite(siteId, token, type);
        if (r && r.success) { ok++; addLog(`✓ ${name} — installed`, 'ok'); }
        else addLog(`✗ ${name} — ${r?.error || 'failed'}`, 'err');
      } catch (err) {
        addLog(`✗ ${name} — ${err?.error || err?.message || 'failed'}`, 'err');
      }
      done++;
      setProgress(Math.round((done / selected.length) * 100));
    }
    addLog(`\n✓ Done! ${ok}/${selected.length} site(s) deployed.`, ok === selected.length ? 'ok' : 'warn');
    toast.success(`Deployed to ${ok}/${selected.length} sites`);
    setDeploying(false);
  };

  const handleDrop = e => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f?.name.endsWith('.zip')) setFile(f);
    else toast.error('Only .zip files allowed');
  };

  const currentList = type === 'plugin' ? uniquePlugins : uniqueThemes;

  return (
    <>
      <div className="page-header">
        <div><h1>Upload & Deploy</h1><p>Filter by plugin/theme → see which sites need update → bulk update</p></div>
        <div className="header-actions">
          <Button className="btn" onClick={loadAll} loading={loadingData}>
            <RefreshCw size={13}/> {loadingData?'Loading...':'Refresh data'}
          </Button>
        </div>
      </div>

      <div className="page-content">
        {/* Type toggle */}
        <div style={{display:'flex',gap:8,marginBottom:12}}>
          <button className={`btn${type==='plugin'?' primary':''}`} onClick={()=>{setType('plugin');setSelectedPlugin(null);setSelected([]);}} style={{flex:1,justifyContent:'center',padding:'10px'}}>
            🧩 Plugin Manager
          </button>
          <button className={`btn${type==='theme'?' primary':''}`} onClick={()=>{setType('theme');setSelectedPlugin(null);setSelected([]);}} style={{flex:1,justifyContent:'center',padding:'10px'}}>
            🎨 Theme Manager
          </button>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>

          {/* LEFT — Site list */}
          <div>
            {/* Plugin/Theme selector */}
            <div className="card" style={{marginBottom:10}}>
              <div className="card-head">
                <h3>{type==='plugin'?'🧩 Select Plugin':'🎨 Select Theme'}</h3>
                {selectedPlugin && <span style={{fontSize:11,color:'var(--text3)'}}>on {sitesAll.length} site{sitesAll.length!==1?'s':''} · {sitesWithUpdate.length} need update</span>}
              </div>
              <div style={{padding:'10px 14px',position:'relative'}}>
                <select
                  value={selectedPlugin?.name || ''}
                  onChange={e => {
                    const item = currentList.find(x => x.name === e.target.value);
                    if (item) handlePluginSelect(item);
                    else { setSelectedPlugin(null); setSelected([]); }
                  }}
                  style={{width:'100%',background:'var(--bg3)',border:`1px solid ${selectedPlugin?'var(--accent)':'var(--border2)'}`,borderRadius:8,padding:'8px 12px',fontSize:12,color:selectedPlugin?'var(--accent-text)':'var(--text3)',cursor:'pointer',outline:'none'}}>
                  <option value=''>-- Choose {type} --</option>
                  {currentList.map((item,i) => {
                    const updateCount = type==='plugin'
                      ? allPlugins.filter(p=>p.name===item.name&&p.update_available).length
                      : allThemes.filter(t=>t.name===item.name&&t.update_available).length;
                    return (
                      <option key={i} value={item.name}>
                        {item.name}{updateCount > 0 ? ` (${updateCount} updates)` : ' ✓'}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {/* All sites that have this plugin/theme */}
            <div className="card">
              <div className="card-head">
                <h3><Globe size={14}/> {selectedPlugin ? 'Sites with this ' + type : 'Sites'}</h3>
                {sitesAll.length > 0 && (
                  <div style={{display:'flex',gap:6}}>
                    {sitesWithUpdate.length > 0 && <button className="btn sm" onClick={selectNeedingUpdate}>Needs update ({sitesWithUpdate.length})</button>}
                    <button className="btn sm" onClick={selectAll}>Select all ({sitesAll.length})</button>
                  </div>
                )}
              </div>
              <div style={{padding:'8px 14px',maxHeight:340,overflowY:'auto'}}>
                {!selectedPlugin && (
                  <div style={{padding:'20px 0',textAlign:'center',fontSize:12,color:'var(--text3)'}}>
                    ← Select a {type} above, then pick the sites to update or deploy to
                  </div>
                )}
                {selectedPlugin && sitesAll.length===0 && (
                  <div style={{padding:'20px 0',textAlign:'center',fontSize:12,color:'var(--text3)'}}>
                    “{selectedPlugin.name}” isn’t installed on any connected site.
                  </div>
                )}
                {selectedPlugin && sitesAll.length>0 && sitesWithUpdate.length===0 && (
                  <div style={{padding:'8px 4px 12px',fontSize:11,color:'var(--text3)'}}>
                    WordPress reports no repo update for “{selectedPlugin.name}” (normal for premium plugins). Use <strong>Deploy custom .zip</strong> below to push your version to the sites you select.
                  </div>
                )}
                {sitesAll.map((item,i) => (
                  <div key={i} onClick={()=>toggleSite(item.site_id)}
                    style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',marginBottom:5,background:selected.includes(item.site_id)?'var(--accent-bg)':'var(--bg3)',border:`1px solid ${selected.includes(item.site_id)?'var(--accent2)':'var(--border)'}`,borderRadius:8,cursor:'pointer',transition:'all .15s'}}>
                    <input type="checkbox" checked={selected.includes(item.site_id)} readOnly style={{accentColor:'var(--accent)',flexShrink:0}}/>
                    <Flag country={item.country}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,color:selected.includes(item.site_id)?'var(--accent-text)':'var(--text)',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.site_name}</div>
                      <div style={{fontSize:10,color:'var(--text3)',marginTop:1}}>
                        v{item.version}{item.update_available && item.new_version ? <> → <span style={{color:'var(--green-text)'}}>{item.new_version}</span></> : ''}
                      </div>
                    </div>
                    <span className={`badge ${item.update_available?'amber':'green'}`} style={{flexShrink:0}}>{item.update_available?'Update':'Up to date'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT — Deploy console */}
          <div style={{display:'flex',flexDirection:'column',gap:10}}>

            {/* Bulk update (no zip) */}
            {(() => {
              const updatableSelected = selected.filter(id => sitesAll.find(s => s.site_id === id && s.update_available)).length;
              return (
            <div className="card">
              <div className="card-head">
                <h3><Zap size={14}/> Bulk update (from WP repo)</h3>
                <Button className="btn primary" onClick={handleBulkUpdate}
                  loading={updating} disabled={!selectedPlugin || !updatableSelected}>
                  <Zap size={13}/> {updating?'Updating...':`Update ${updatableSelected} site${updatableSelected!==1?'s':''}`}
                </Button>
              </div>
              <div style={{padding:'10px 14px',fontSize:11,color:'var(--text3)'}}>
                For free/repo plugins only. {selectedPlugin && !updatableSelected && sitesWithUpdate.length===0
                  ? <span style={{color:'var(--amber-text)'}}>“{selectedPlugin.name}” has no repo update — use “Deploy custom .zip” below. →</span>
                  : 'Select sites with an update, then click Update. No zip needed.'}
              </div>
            </div>
              );
            })()}

            {/* ZIP deploy */}
            <div className="card" style={selectedPlugin && sitesWithUpdate.length===0 ? {borderColor:'var(--accent2)'} : undefined}>
              <div className="card-head">
                <h3><FileArchive size={14}/> Deploy custom .zip {selectedPlugin && sitesWithUpdate.length===0 && <span className="badge blue" style={{marginLeft:6}}>use this for {selectedPlugin.name}</span>}</h3>
                <Button className="btn success" onClick={handleDeploy}
                  loading={deploying} disabled={!file||!selected.length}>
                  <UploadIcon size={13}/> {deploying?'Deploying...':`Run deploy${selected.length?` (${selected.length})`:''}`}
                </Button>
              </div>
              <div style={{padding:'10px 14px'}}>
                <div className={`drop-zone${file?' active':''}`}
                  onClick={()=>fileRef.current.click()}
                  onDragOver={e=>e.preventDefault()} onDrop={handleDrop}
                  style={{padding:'16px',marginBottom:8}}>
                  <div className="dz-icon"><UploadIcon size={22}/></div>
                  <div className="dz-title" style={{fontSize:12}}>{file?file.name:`Drop ${type} .zip here`}</div>
                  <div className="dz-sub">{file?`${(file.size/1024).toFixed(1)} KB`:'or click to browse'}</div>
                </div>
                <input ref={fileRef} type="file" accept=".zip" style={{display:'none'}} onChange={e=>setFile(e.target.files[0])}/>
              </div>
            </div>

            {/* Console */}
            <div className="card" style={{flex:1}}>
              <div className="card-head"><h3>Console log</h3></div>
              <div style={{padding:'10px 14px'}}>
                {progress>0 && (
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                    <div className="progress-wrap" style={{flex:1}}>
                      <div className="progress-bar" style={{width:`${progress}%`}}></div>
                    </div>
                    <span style={{fontSize:11,color:'var(--text3)'}}>{progress}%</span>
                  </div>
                )}
                <div className="log-box">
                  {!log.length&&<span className="info">// Select {type} → select sites → Update or Deploy</span>}
                  {log.map((l,i)=><div key={i} className={l.cls}>{l.msg}</div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
